import { SubgraphType } from 'config/subgraph'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { ChartDayData } from 'types/analytics'
import { AnalyticsChartSeries, buildAnalyticsChartSeries } from 'utils/analytics/analyticsChartSeries'

import type {
  GetV3GlobalChartQuery,
  GetV3PoolChartQuery,
  GetV3TokenChartQuery,
} from '../../queries/analytics/v3/generated'
import {
  useGetV3GlobalChartQuery,
  useGetV3PoolChartQuery,
  useGetV3TokenChartQuery,
} from '../../queries/analytics/v3/generated'

export const v3ChartQueryKey = (
  chainId: number,
  startTime: number,
  skip: number,
  poolAddress?: string,
  tokenAddress?: string,
  block?: number
) => ['analytics', 'v3ChartData', chainId, startTime, skip, poolAddress, tokenAddress, block] as const

const mapDayData = (
  dayData: Array<{
    date: number
    volumeUSD: string | number | null | undefined
    tvlUSD: string | number | null | undefined
    feesUSD: string | number | null | undefined
    priceUSD?: string | number | null | undefined
  }>
): AnalyticsChartSeries => {
  const toNumber = (value: string | number | null | undefined): number => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0
    }

    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value)
      return Number.isFinite(parsed) ? parsed : 0
    }

    return 0
  }

  const mapped: ChartDayData[] = dayData
    .map((item) => {
      const priceUSDRaw = item.priceUSD
      const priceUSD = priceUSDRaw === null || priceUSDRaw === undefined ? undefined : toNumber(priceUSDRaw)

      return {
        date: item.date,
        volumeUSD: toNumber(item.volumeUSD),
        tvlUSD: toNumber(item.tvlUSD),
        feesUSD: toNumber(item.feesUSD),
        priceUSD: priceUSD !== undefined && Number.isFinite(priceUSD) ? priceUSD : undefined,
      }
    })
    .sort((a, b) => b.date - a.date)

  return buildAnalyticsChartSeries(mapped)
}

export const useV3ChartData = (
  startTime: number,
  skip: number,
  options?: {
    enabled?: boolean
    poolAddress?: string
    tokenAddress?: string
  }
) => {
  const { chainId } = useConnection()
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)

  const endpoint = subgraphConfig.endpoint

  const baseEnabled = (options?.enabled ?? true) && Boolean(subgraphConfig)
  const poolAddress = options?.poolAddress
  const tokenAddress = options?.tokenAddress

  const poolEnabled = baseEnabled && Boolean(poolAddress)
  const tokenEnabled = baseEnabled && !poolAddress && Boolean(tokenAddress)
  const globalEnabled = baseEnabled && !poolAddress && !tokenAddress

  // Current data query
  const poolQuery = useGetV3PoolChartQuery<AnalyticsChartSeries>(
    {
      endpoint,
    },
    {
      startTime,
      skip,
      poolAddress: poolAddress ?? '',
    },
    {
      enabled: poolEnabled,
      queryKey: v3ChartQueryKey(chainId, startTime, skip, poolAddress ?? undefined, undefined),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      experimental_prefetchInRender: true,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      select: (response: GetV3PoolChartQuery) =>
        mapDayData(
          response.poolDayDatas.map((poolDayData) => ({
            date: poolDayData.date,
            volumeUSD: poolDayData.volumeUSD,
            tvlUSD: poolDayData.tvlUSD,
            feesUSD: poolDayData.feesUSD,
          }))
        ),
    }
  )

  const tokenQuery = useGetV3TokenChartQuery<AnalyticsChartSeries>(
    {
      endpoint,
    },
    {
      startTime,
      skip,
      tokenAddress: tokenAddress ?? '',
    },
    {
      enabled: tokenEnabled,
      queryKey: v3ChartQueryKey(chainId, startTime, skip, undefined, tokenAddress ?? undefined),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV3TokenChartQuery) =>
        mapDayData(
          response.tokenDayDatas.map(({ date, volumeUSD, tvlUSD, feesUSD, priceUSD }) => ({
            date,
            volumeUSD,
            tvlUSD,
            feesUSD,
            priceUSD,
          }))
        ),
    }
  )

  const globalQuery = useGetV3GlobalChartQuery<AnalyticsChartSeries>(
    {
      endpoint,
    },
    {
      startTime,
      skip,
    },
    {
      enabled: globalEnabled,
      queryKey: v3ChartQueryKey(chainId, startTime, skip, undefined, undefined),
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV3GlobalChartQuery) =>
        mapDayData(
          response.uniswapDayDatas.map(({ date, volumeUSD, tvlUSD, feesUSD }) => ({
            date,
            volumeUSD,
            tvlUSD,
            feesUSD,
          }))
        ),
    }
  )

  const activeQuery = poolEnabled ? poolQuery : tokenEnabled ? tokenQuery : globalQuery

  const prepared = activeQuery.data
  const rawData = prepared?.raw ?? []

  return {
    ...activeQuery,
    data: rawData,
    prepared,
    latestEntry: prepared?.latestEntry,
    tvlSeries: prepared?.tvl,
    volumeSeries: prepared?.volume,
    feesSeries: prepared?.fees,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    error: activeQuery.error ?? undefined,
  }
}
